import { HttpClient } from '@angular/common/http';
import { TranslateLoader } from '@ngx-translate/core';
import { catchError, forkJoin, map, Observable, of } from 'rxjs';
import { i18nBasePath, i18nClientPath } from '../client-config';

/**
 * Carga las traducciones para un idioma combinando:
 *   1. El fichero base común `/i18n/base/<lang>.json`
 *   2. Los overrides del cliente activo  `/i18n/<cliente>/<lang>.json`
 *
 * Si el override del cliente no existe (404) se ignora silenciosamente
 * y se devuelve únicamente el base. El merge es profundo y los valores
 * del override prevalecen sobre los del base.
 */
export class ClientAwareTranslateLoader implements TranslateLoader {
  constructor(private http: HttpClient) {}

  getTranslation(lang: string): Observable<Record<string, unknown>> {
    const baseUrl = `${i18nBasePath()}/${lang}.json`;
    const clientUrl = `${i18nClientPath()}/${lang}.json`;

    return forkJoin({
      base: this.http
        .get<Record<string, unknown>>(baseUrl)
        .pipe(catchError(() => of<Record<string, unknown>>({}))),
      client: this.http
        .get<Record<string, unknown>>(clientUrl)
        .pipe(catchError(() => of<Record<string, unknown>>({}))),
    }).pipe(map(({ base, client }) => deepMerge(base, client)));
  }
}

/**
 * Merge profundo: combina dos objetos recursivamente. Las claves del segundo
 * argumento sobrescriben las del primero. Los arrays se sustituyen tal cual
 * (no se fusionan elemento a elemento).
 */
function deepMerge(
  target: Record<string, unknown>,
  source: Record<string, unknown>
): Record<string, unknown> {
  const result: Record<string, unknown> = { ...target };
  for (const key of Object.keys(source)) {
    const sourceValue = source[key];
    const targetValue = result[key];
    if (isPlainObject(sourceValue) && isPlainObject(targetValue)) {
      result[key] = deepMerge(
        targetValue as Record<string, unknown>,
        sourceValue as Record<string, unknown>
      );
    } else {
      result[key] = sourceValue;
    }
  }
  return result;
}

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}
